/**
 * Node super-admin set management (#38).
 *
 * Reads GET /api/super-admins with the current user id. That endpoint is
 * superadmin-only, so a 403 doubles as the "am I the server admin" signal: we
 * expose `isServerAdmin=false` and callers hide the panel. Any other error is
 * surfaced via `error`.
 *
 *   const { admins, isServerAdmin, loading, error, add, remove } = useSuperAdmins();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function useSuperAdmins() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [admins, setAdmins] = useState([]);
    const [isServerAdmin, setIsServerAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `silent` refreshes after a mutation without flipping `loading`, so the
    // table doesn't dim + spin on every click; only the first load shows it.
    const load = useCallback(({ silent = false } = {}) => {
        if (!userId) { setLoading(false); return Promise.resolve(); }
        if (!silent) setLoading(true);
        return api.get('/api/super-admins', { params: { user_id: userId } })
            .then(data => {
                setAdmins(Array.isArray(data) ? data : []);
                setIsServerAdmin(true);
                setError(null);
            })
            .catch(err => {
                if (err.status === 403) {
                    setIsServerAdmin(false);   // not the server admin — panel stays hidden
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const add = useCallback(targetUserId => {
        const path = `/api/super-admins/${encodeURIComponent(targetUserId)}`;
        return api.post(path, undefined, { params: { user_id: userId } }).then(() => load({ silent: true }));
    }, [api, userId, load]);

    const remove = useCallback(targetUserId => {
        const path = `/api/super-admins/${encodeURIComponent(targetUserId)}`;
        return api.del(path, { params: { user_id: userId } }).then(() => load({ silent: true }));
    }, [api, userId, load]);

    return {
        admins,
        isServerAdmin,
        loading,
        error,
        refresh: load,
        add,
        remove,
    };
}

export default useSuperAdmins;
